import { useTranslation } from "react-i18next";
import { GENDER_LABELS } from "@/lib/constants";

function OverviewItem({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<p className="text-muted-foreground/75 text-xs uppercase tracking-wide">
				{label}
			</p>
			<p className="font-medium uppercase text-pretty">{value}</p>
		</div>
	);
}

export interface ScheduleOverviewProps {
	registrationNumber?: string | null;
	patientName?: string | null;
	patientGender?: string | null;
	cityName?: string | null;
	serviceName?: string | null;
	packageName?: string | null;
	totalVisits?: number | null;
}

/**
 * The booking's at-a-glance header (reg. number, patient, city, service,
 * package). Shared by bulk and single reschedule so both read identically.
 */
export function ScheduleOverview({
	registrationNumber,
	patientName,
	patientGender,
	cityName,
	serviceName,
	packageName,
	totalVisits,
}: ScheduleOverviewProps) {
	const { t } = useTranslation("appointments-form");
	const na = t("common.not_applicable", "N/A");

	const genderPrefix =
		GENDER_LABELS.find((g) => g.title === patientGender)?.titlePrefix ?? "";
	const patientLabel = patientName
		? `${genderPrefix} ${patientName}`.trim()
		: na;

	return (
		<div className="border rounded-lg bg-background p-4">
			<p className="font-semibold uppercase text-sm text-muted-foreground/75 mb-4">
				{t("common.overview", "Overview")}
			</p>
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
				<OverviewItem
					label={t(
						"appt_schedule.fields.registration_number.label",
						"Reg. Number",
					)}
					value={registrationNumber || na}
				/>
				<OverviewItem
					label={t("patient_profile.fields.name.label", "Patient Name")}
					value={patientLabel}
				/>
				<OverviewItem
					label={t("appt_schedule.fields.city.label", "City")}
					value={cityName || na}
				/>
				<OverviewItem
					label={t("appt_schedule.fields.service.label", "Service")}
					value={serviceName?.replaceAll("_", " ") || na}
				/>
				<OverviewItem
					label={t("appt_schedule.fields.package.label", "Package")}
					value={
						packageName
							? `${packageName} — ${totalVisits ?? 0} ${t("common.visits", "Visit(s)")}`
							: na
					}
				/>
			</div>
		</div>
	);
}
